import { type NextPage } from 'next';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import { ReactElement, ReactNode } from 'react';

import AuthenticatedPage from '@/components/auth/AuthenticatedPage';
import AppProviders from '@/components/layout/AppProviders';
import LayoutScripts from '@/components/layout/LayoutScripts';
import enMessages from '@/lang/en.json';
import { getPageTitle } from '@/utils/environment';

import 'react-datepicker/dist/react-datepicker.min.css';

import { figtreeFont } from '../fonts';

// Define the type for pages with layouts
export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
	getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
	Component: NextPageWithLayout;
};

/**
 * Pages can define custom layouts using the getLayout pattern:
 *   MyPage.getLayout = (page) => <Layout>{page}</Layout>
 */
export default function App({ Component, pageProps, router }: AppPropsWithLayout): ReactElement {
	const locale = 'en';
	// Use messages from pageProps if provided (for SSR/SSG pages), otherwise use imported messages
	const messages = pageProps.messages || enMessages;

	// Use the page-specific layout if provided, otherwise use default
	// IMPORTANT: Call getLayout with the page element to preserve layout across navigations
	const getLayout = Component.getLayout ?? ((page: ReactElement) => page);
	const pageWithLayout = getLayout(<Component {...pageProps} />);

	// Don't wrap root page with AuthenticatedPage - it handles its own redirect logic
	// /login DOES get wrapped to initialize Redux, but AuthenticatedPage skips auth checks for /login paths
	const isRootPage = router.pathname === '/';
	const content = isRootPage ? pageWithLayout : <AuthenticatedPage>{pageWithLayout}</AuthenticatedPage>;

	return (
		<>
			<Head>
				<title>{getPageTitle()}</title>
				<meta name="description" content="Partner portal for Calm businesses and affiliates" />
			</Head>
			<main className={`${figtreeFont.variable} ${figtreeFont.className}`}>
				<AppProviders locale={locale} messages={messages}>
					{content}
				</AppProviders>
				<LayoutScripts />
			</main>
		</>
	);
}
